/**
 * React Query Client Configuration
 *
 * Centralized query client with sensible defaults for the MOE e-Services platform.
 * Features:
 * - Automatic retry with exponential backoff
 * - Stale-while-revalidate for instant UI
 * - Request deduplication
 * - Background refetching
 */

import { QueryClient } from "@tanstack/react-query";

/**
 * Determine if an error is retryable
 * Don't retry on auth errors, not found, or abort errors
 */
function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  // Max 2 retries
  if (failureCount >= 2) return false;

  // Check for abort/timeout errors - don't retry these
  if (error instanceof Error) {
    if (error.name === "AbortError") return false;
    if (error.message.includes("aborted")) return false;
    if (error.message.includes("timeout")) return false;
  }

  // Check for HTTP status codes
  const status = (error as { status?: number })?.status;
  if (status === 401 || status === 403 || status === 404) return false;

  return true;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,

      // Cache data for 5 minutes after becoming unused
      gcTime: 5 * 60 * 1000,

      // Smart retry logic
      retry: shouldRetryQuery,
      retryDelay: (attemptIndex: number) =>
        Math.min(1000 * 2 ** attemptIndex, 10000),

      // Refetch on window focus (good for data freshness)
      refetchOnWindowFocus: true,

      // Refetch on reconnect
      refetchOnReconnect: "always",

      // Don't refetch on mount if data is still fresh
      refetchOnMount: true,

      // Network mode - always try to fetch, even if offline
      networkMode: "always",
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

/**
 * Query key factory for consistent cache key management
 * Using a factory pattern ensures type-safe and consistent query keys
 */
export const queryKeys = {
  // Dashboard
  dashboard: {
    all: ["dashboard"] as const,
    // Keyed by user: nothing clears this cache on sign-out, so a key without
    // the user would hand one person's figures to the next in the same tab.
    overview: (userId: string, timeRange: string) =>
      ["dashboard", "overview", userId, timeRange] as const,
  },

  // Applications
  applications: {
    all: ["applications"] as const,
    lists: () => [...queryKeys.applications.all, "list"] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.applications.lists(), filters] as const,
    details: () => [...queryKeys.applications.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.applications.details(), id] as const,
    statusCounts: () =>
      [...queryKeys.applications.all, "statusCounts"] as const,
    hub: (userId: string) =>
      [...queryKeys.applications.all, "hub", userId] as const,
    viewedStatus: (userId: string) =>
      [...queryKeys.applications.all, "viewedStatus", userId] as const,
  },

  // Services
  services: {
    all: ["services"] as const,
    lists: () => [...queryKeys.services.all, "list"] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.services.lists(), filters] as const,
    details: () => [...queryKeys.services.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.services.details(), id] as const,
    categories: () => [...queryKeys.services.all, "categories"] as const,
  },

  // Users
  users: {
    all: ["users"] as const,
    lists: () => [...queryKeys.users.all, "list"] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.users.all, "detail", id] as const,
  },

  // Notifications
  notifications: {
    all: ["notifications"] as const,
    list: (userId: string) =>
      [...queryKeys.notifications.all, "list", userId] as const,
    unreadCount: (userId: string) =>
      [...queryKeys.notifications.all, "unreadCount", userId] as const,
  },

  // Reports & Analytics
  reports: {
    all: ["reports"] as const,
    figures: (
      userId: string,
      advanced: boolean,
      filters: Record<string, unknown>,
    ) =>
      [...queryKeys.reports.all, "figures", userId, advanced, filters] as const,
    services: () => [...queryKeys.reports.all, "services"] as const,
    regions: () => [...queryKeys.reports.all, "regions"] as const,
    export: (type: string, filters: Record<string, unknown>) =>
      [...queryKeys.reports.all, "export", type, filters] as const,
  },
} as const;
